<template>
  <v-dialog
    v-model="show"
    max-width="560"
    scrollable
    :fullscreen="$vuetify.breakpoint.smAndDown"
    persistent
  >
    <v-card class="ds-dialog">
      <v-card-title class="ds-dialog-header py-4">
        <v-icon color="white" class="mr-3">mdi-calendar-account</v-icon>
        <span class="ds-dialog-title">
          {{ editando ? "Editar compromisso" : "Novo compromisso" }}
        </span>
        <v-spacer />
        <v-btn class="close-btn" icon color="white" @click="fechar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="ds-dialog-body">
        <v-form ref="form" v-model="valido">
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-calendar-account</v-icon>
                <span>Compromisso</span>
              </div>
            </div>
            <p class="cm-ajuda">
              Bloqueia a sua agenda num horário específico. Ninguém consegue marcar
              paciente nesse intervalo, e ele aparece no calendário em laranja.
            </p>

            <v-text-field
              v-model="form.title"
              label="Nome do compromisso"
              placeholder="Reunião, imprevisto, compromisso pessoal…"
              prepend-inner-icon="mdi-format-title"
              outlined
              dense
              color="primary"
              autofocus
              counter="255"
              :rules="[(v) => !!(v && v.trim()) || 'Dê um nome ao compromisso']"
            />
          </section>

          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-clock-outline</v-icon>
                <span>Horário</span>
              </div>
            </div>
            <v-row dense>
              <v-col cols="12" sm="4">
                <v-menu
                  v-model="menuData"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="dataFormatada"
                      label="Dia"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      color="primary"
                      hide-details="auto"
                      v-bind="attrs"
                      v-on="on"
                      :rules="[(v) => !!v || 'Escolha o dia']"
                    />
                  </template>
                  <v-date-picker
                    v-model="form.date"
                    locale="pt-BR"
                    :min="hoje"
                    @input="menuData = false"
                  />
                </v-menu>
              </v-col>
              <v-col cols="6" sm="4">
                <v-autocomplete
                  v-model="form.start_time"
                  :items="horarios"
                  label="Início"
                  prepend-inner-icon="mdi-clock-outline"
                  outlined
                  dense
                  color="primary"
                  hide-details="auto"
                  :rules="[(v) => !!v || 'Obrigatório']"
                  @change="ajustarFim"
                />
              </v-col>
              <v-col cols="6" sm="4">
                <v-autocomplete
                  v-model="form.end_time"
                  :items="horariosFim"
                  label="Fim"
                  prepend-inner-icon="mdi-clock-end"
                  outlined
                  dense
                  color="primary"
                  hide-details="auto"
                  :rules="[
                    (v) => !!v || 'Obrigatório',
                    (v) => v > form.start_time || 'Fim deve ser depois do início',
                  ]"
                />
              </v-col>
            </v-row>
          </section>

          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-text</v-icon>
                <span>Detalhes</span>
                <span class="ds-form-section__optional">opcional</span>
              </div>
            </div>
            <v-select
              v-if="clinicas.length > 1"
              v-model="form.clinic_id"
              :items="clinicas"
              item-text="name"
              item-value="id"
              label="Clínica"
              prepend-inner-icon="mdi-hospital-building"
              outlined
              dense
              color="primary"
              clearable
              hint="Em branco, o compromisso vale para todas as suas clínicas."
              persistent-hint
              class="mb-3"
            />

            <v-textarea
              v-model="form.description"
              label="Observação (opcional)"
              prepend-inner-icon="mdi-text"
              outlined
              dense
              color="primary"
              rows="2"
              auto-grow
              hide-details="auto"
            />

            <v-alert v-if="erro" type="warning" dense text class="mt-3 mb-0">
              {{ erro }}
            </v-alert>
          </section>
        </v-form>
      </v-card-text>

      <v-card-actions class="ds-dialog-actions">
        <v-btn outlined color="primary" @click="fechar">
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-spacer />
        <v-btn color="primary" depressed :loading="salvando" :disabled="!valido" @click="salvar">
          <v-icon left>mdi-content-save</v-icon>
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Criação e edição de COMPROMISSO do médico — salvia-772.
 *
 * Bloqueio relâmpago da própria agenda. Difere da parametrização de agenda
 * (`AgendaConfigDialog`), que cria disponibilidade recorrente e travas de
 * período com motivo catalogado; aqui é pontual, tem nome livre e aparece no
 * calendário como evento.
 *
 * O backend grava como `events` sem paciente, então três coisas passam a valer
 * de graça: o horário sai do verde no seletor, ninguém consegue agendar por
 * cima, e o compromisso é editável/removível pela própria agenda.
 */
import {
  createCommitment,
  updateCommitment,
} from "@/services/commitmentService";
import { getMyClinics } from "@/services/clinicService";
import { useSnackbar } from "@/composables/useSnackbar";

/** Grade de 15 em 15 minutos, como o resto da agenda. */
function gradeDeHorarios() {
  const saida = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      saida.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return saida;
}

export default {
  name: "CommitmentDialog",

  // `useSnackbar` e composable, nao mixin — mesmo padrao do FitInPendingDialog.
  setup() {
    const { success, erroDeRequisicao } = useSnackbar();
    return { success, erroDeRequisicao };
  },

  props: {
    value: { type: Boolean, default: false },
    /** Compromisso a editar; null cria um novo. */
    commitment: { type: Object, default: null },
  },

  data() {
    return {
      valido: false,
      salvando: false,
      menuData: false,
      erro: null,
      clinicas: [],
      horarios: gradeDeHorarios(),
      form: {
        title: "",
        date: null,
        start_time: "",
        end_time: "",
        clinic_id: null,
        description: "",
      },
    };
  },

  computed: {
    show: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },

    editando() {
      return !!(this.commitment && this.commitment.id);
    },

    hoje() {
      return new Date().toISOString().slice(0, 10);
    },

    dataFormatada() {
      if (!this.form.date) return "";
      const [y, m, d] = this.form.date.split("-");
      return `${d}/${m}/${y}`;
    },

    /** O fim só pode vir depois do início — não adianta oferecer o resto. */
    horariosFim() {
      if (!this.form.start_time) return this.horarios;
      return this.horarios.filter((h) => h > this.form.start_time);
    },
  },

  watch: {
    value(aberto) {
      if (aberto) this.preparar();
    },
  },

  methods: {
    async preparar() {
      this.erro = null;
      this.clinicas = await getMyClinics().catch(() => []);

      if (this.editando) {
        const c = this.commitment;
        const inicio = new Date(c.start);
        const fim = new Date(c.end);
        this.form = {
          title: c.title || "",
          date: this.paraDataLocal(inicio),
          start_time: this.paraHoraLocal(inicio),
          end_time: this.paraHoraLocal(fim),
          clinic_id: c.clinic_id || null,
          description: c.description || "",
        };
      } else {
        this.form = {
          title: "",
          date: this.hoje,
          start_time: "",
          end_time: "",
          clinic_id: this.clinicas.length === 1 ? this.clinicas[0].id : null,
          description: "",
        };
      }

      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },

    paraDataLocal(d) {
      return [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, "0"),
        String(d.getDate()).padStart(2, "0"),
      ].join("-");
    },

    paraHoraLocal(d) {
      return `${String(d.getHours()).padStart(2, "0")}:${String(
        d.getMinutes(),
      ).padStart(2, "0")}`;
    },

    /** Escolheu o início? Sugere uma hora de duração, e nunca um fim inválido. */
    ajustarFim() {
      if (!this.form.start_time) return;
      const i = this.horarios.indexOf(this.form.start_time);
      if (!this.form.end_time || this.form.end_time <= this.form.start_time) {
        this.form.end_time = this.horarios[Math.min(i + 4, this.horarios.length - 1)];
      }
    },

    async salvar() {
      if (!this.$refs.form.validate()) return;

      this.salvando = true;
      this.erro = null;

      const payload = {
        ...this.form,
        title: this.form.title.trim(),
        // O fuso do navegador, como no agendamento. Sem ele, "14:00" seria
        // interpretado no fuso do servidor e o bloqueio cairia na hora errada.
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      try {
        if (this.editando) {
          await updateCommitment(this.commitment.id, payload);
          this.success("Compromisso atualizado.");
        } else {
          await createCommitment(payload);
          this.success("Compromisso criado.");
        }

        this.$emit("saved");
        this.fechar();
      } catch (e) {
        // 409 é choque com paciente já agendado: a mensagem do servidor diz o
        // horário, e é ela que ajuda o médico a resolver.
        this.erro =
          e?.response?.data?.message ||
          "Não foi possível salvar o compromisso. Tente de novo.";
      } finally {
        this.salvando = false;
      }
    },

    fechar() {
      this.show = false;
      this.erro = null;
    },
  },
};
</script>

<style scoped>
.cm-ajuda {
  font-size: 13px;
  color: var(--ds-text-soft, #78909c);
  margin-bottom: 12px;
}
</style>
